/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 filter dropdown (design-reference table toolbar: a quiet button with the
 * current choice and a chevron). Replaces the outlined MUI Select with its
 * notched floating label on v2 list and board views.
 *
 * Drop-in for a controlled select: `value`, `onChange(value)`, `options`.
 *
 * @param {object} props
 * @param {string|number} props.value Current value; compared to option values with String().
 * @param {(value: any) => void} props.onChange Called with the chosen option's value.
 * @param {Array<{value: any, label: React.ReactNode, hint?: React.ReactNode, disabled?: boolean}>|string[]} props.options
 * @param {string} [props.label] Inline prefix ("Status: Open") or caption above; also the accessible name.
 * @param {"inline"|"above"} [props.labelPlacement="inline"]
 * @param {string} [props.placeholder="Select"] Shown when no option matches `value`.
 * @param {"medium"|"small"} [props.size="medium"] 36px or 30px.
 * @param {boolean} [props.fullWidth=false]
 * @param {number|string} [props.minWidth]
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.id]
 * @param {number} [props.menuMaxHeight=320]
 * @param {object} [props.sx]
 */

import { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Check, ChevronDown } from "lucide-react";
import { v2 as t, v2Density as d, v2Tones, V2_FONT_FAMILY } from "styles/tokens/v2";

export const normalizeOptions = (options) =>
  (options || []).map((o) => (o !== null && typeof o === "object" ? o : { value: o, label: String(o) }));

const same = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

export default function FilterSelect({
  value,
  onChange,
  options,
  label,
  labelPlacement,
  placeholder,
  size,
  fullWidth,
  minWidth,
  disabled,
  id,
  menuMaxHeight,
  sx,
}) {
  const autoId = useId();
  const baseId = id || `filter-select-${autoId.replace(/:/g, "")}`;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const opts = normalizeOptions(options);
  const current = opts.find((o) => same(o.value, value)) || null;
  const small = size === "small";
  const height = small ? d.controlHeightSmallPx : d.controlHeightPx;
  const inline = labelPlacement !== "above" && Boolean(label);
  const captionId = label && !inline ? `${baseId}-label` : undefined;

  const pick = (o) => {
    setOpen(false);
    if (o.disabled) return;
    onChange(o.value);
  };

  return (
    <Box sx={{ display: fullWidth ? "flex" : "inline-flex", flexDirection: "column", gap: 0.5, minWidth, width: fullWidth ? "100%" : undefined, ...sx }}>
      {label && !inline && (
        <Box component="span" id={captionId} sx={{ fontFamily: V2_FONT_FAMILY, fontSize: 12, fontWeight: 600, color: t.text2 }}>
          {label}
        </Box>
      )}
      <ButtonBase
        ref={anchorRef}
        id={baseId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? `${baseId}-menu` : undefined}
        aria-label={inline ? `${label}: ${current ? (typeof current.label === "string" ? current.label : String(current.value)) : placeholder}` : undefined}
        aria-labelledby={captionId ? `${captionId} ${baseId}` : undefined}
        onClick={() => setOpen(true)}
        sx={{
          height,
          width: fullWidth ? "100%" : undefined,
          minWidth: 0,
          px: small ? 1.25 : 1.5,
          gap: 1,
          justifyContent: "space-between",
          borderRadius: `${d.radiusPx}px`,
          border: `1px solid ${open ? t.primary : t.border2}`,
          bgcolor: disabled ? t.surface2 : t.surface,
          color: disabled ? t.textDisabled : t.text,
          fontFamily: V2_FONT_FAMILY,
          fontSize: small ? 13 : 14,
          fontWeight: 500,
          textAlign: "left",
          transition: "border-color 120ms",
          "&:hover": { borderColor: disabled ? t.border2 : t.text3 },
          "&.Mui-focusVisible": { outline: `2px solid ${t.link}`, outlineOffset: 1 },
        }}
      >
        <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {inline && (
            <Box component="span" sx={{ color: t.text2, fontWeight: 500 }}>
              {`${label}: `}
            </Box>
          )}
          {current ? (
            <Box component="span" sx={{ fontWeight: 600 }}>{current.label}</Box>
          ) : (
            <Box component="span" sx={{ color: t.text3 }}>{placeholder}</Box>
          )}
        </Box>
        <ChevronDown size={16} aria-hidden="true" color={disabled ? t.textDisabled : t.text3} style={{ flexShrink: 0 }} />
      </ButtonBase>
      <Menu
        id={`${baseId}-menu`}
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ role: "listbox", "aria-labelledby": captionId || baseId, dense: true }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: anchorRef.current ? anchorRef.current.offsetWidth : 160,
            maxHeight: menuMaxHeight,
            borderRadius: `${d.cardRadiusPx}px`,
            border: `1px solid ${t.border}`,
            boxShadow: t.shadowMenu,
            bgcolor: t.surface,
          },
        }}
      >
        {opts.map((o) => {
          const selected = Boolean(current) && same(o.value, current.value);
          return (
            <MenuItem
              key={String(o.value)}
              role="option"
              aria-selected={selected}
              selected={selected}
              disabled={o.disabled}
              onClick={() => pick(o)}
              sx={{
                minHeight: 36,
                gap: 1,
                fontFamily: V2_FONT_FAMILY,
                fontSize: 14,
                color: t.text,
                "&.Mui-selected": { bgcolor: v2Tones.info.bg, color: v2Tones.info.fg, fontWeight: 600 },
                "&.Mui-selected:hover, &:hover": { bgcolor: selected ? v2Tones.info.bg : t.hover },
              }}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0, whiteSpace: "normal" }}>{o.label}</Box>
              {o.hint && (
                <Box component="span" sx={{ fontSize: 12, color: selected ? v2Tones.info.fg : t.text3, whiteSpace: "nowrap" }}>
                  {o.hint}
                </Box>
              )}
              {selected && <Check size={15} aria-hidden="true" style={{ flexShrink: 0 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

FilterSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        label: PropTypes.node.isRequired,
        hint: PropTypes.node,
        disabled: PropTypes.bool,
      }),
    ])
  ).isRequired,
  label: PropTypes.string,
  labelPlacement: PropTypes.oneOf(["inline", "above"]),
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(["medium", "small"]),
  fullWidth: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  id: PropTypes.string,
  menuMaxHeight: PropTypes.number,
  sx: PropTypes.object,
};

FilterSelect.defaultProps = {
  value: "",
  label: "",
  labelPlacement: "inline",
  placeholder: "Select",
  size: "medium",
  fullWidth: false,
  minWidth: undefined,
  disabled: false,
  id: undefined,
  menuMaxHeight: 320,
  sx: {},
};
